import type { Branded } from "@@/utilities/branded";
import { isNumber } from "@@/utilities/null-checks";
import { parseSafely } from "@@/utilities/parse-with-schema";
import { z } from "zod";
import type { StorageItemImageId } from "../storage-items/storage-item-images/storage-item-image-id";
import type { StorageItemPdf } from "../storage-items/storage-item-pdfs/storage-item-pdf";
import type { ImageRef } from "./image-ref";

type ImageUrl = Branded<string, "ImageUrl">;
const imageUrlZodType = z.string().url() as unknown as z.ZodType<ImageUrl>;
// .refine(url => {
//     const fileType = url.split(".").pop();
//     if (!fileType) return false;
//     return imageFileTypesWithoutHeicHeif.includes(fileType);
// }) as unknown as z.ZodType<ImageUrl>;
const isImageUrl = (input: unknown): input is ImageUrl => {
    if (typeof input !== "string") return false;
    return !!parseSafely({
        schema: imageUrlZodType,
        value: input,
    });
};
const notImageUrl = <T>(input: T): T extends ImageUrl ? never : T => {
    if (isImageUrl(input)) return undefined as never;
    return input as T extends ImageUrl ? never : T;
};
type ImageFormat =
    | "gif"
    | "jp2"
    | "jpg"
    | "json"
    | "jxr"
    | "pjpg"
    | "mp4"
    | "png"
    | "png8"
    | "png32"
    | "webm"
    | "webp"
    | "blurhash";

type ImageUrlCreatorOptions =
    | {
          /**
           * actual image width to request
           * @type {(number | string)}
           */
          readonly imageWidth: number | "full";
          /**
           * actual image height to request
           * @type {(number | string)}
           */
          readonly imageHeight?: number;
          disabled?: boolean;
          blackAndWhite?: boolean;
          /**
           * defaults to true
           *
           * @type {boolean}
           */
          fit?: boolean;
          crop?: "edges";
          /**
           * pick an output image format
           * defaults to jpg
           * @type {ImageFormat}
           */
          format?: ImageFormat;
      }
    | {
          /**
           * actual image width to request
           * @type {(number | string)}
           */
          readonly imageWidth?: number | "full";
          /**
           * actual image height to request
           * @type {(number | string)}
           */
          readonly imageHeight: number;
          disabled?: boolean;
          blackAndWhite?: boolean;
          /**
           * defaults to true
           *
           * @type {boolean}
           */
          fit?: boolean;
          crop?: "edges";
          /**
           * pick an output image format
           * defaults to jpg
           * @type {ImageFormat}
           */
          format?: ImageFormat;
      };

const imgixQuery = (params: ImageUrlCreatorOptions): string => {
    const hasWidthOrHeight =
        isNumber(params.imageWidth) || isNumber(params.imageHeight);
    const quality = 60;
    const queries = Array.from(
        new Set([
            "dpr=2", // retina images (device pixel ratio)
            `q=${quality}`,
            `auto=format`,
            `fm=${params.format || "png"}`,
            ...(isNumber(params.imageWidth) ? [`w=${params.imageWidth}`] : []),
            ...(isNumber(params.imageHeight)
                ? [`h=${params.imageHeight}`]
                : []),
            ...(hasWidthOrHeight && params.fit !== false ? ["fit=crop"] : []),
            ...(hasWidthOrHeight && params.crop ? ["crop=" + params.crop] : []),
            ...(params.disabled ? ["con=-70", "bri=30", "sat=-100"] : []),
            ...(params.blackAndWhite ? ["sat=-100"] : []),
        ]),
    ).join("&");

    return queries;
};

function getImageUrlFactory(mediaEndpoint: string) {
    function imageUrlFactory(
        item:
            | Exclude<ImageRef, StorageItemImageId>
            | StorageItemPdf
            | undefined,
        options: ImageUrlCreatorOptions,
    ): ImageUrl | undefined {
        if (isImageUrl(item)) return item;
        if (!item?.path?.trim()) return undefined;
        const _options = {
            imageWidth: options.imageWidth,
            imageHeight: options.fit ? undefined : options.imageHeight,
            disabled: !!options.disabled,
            blackAndWhite: !!options.blackAndWhite,
            format: options.format ?? "png",
            crop: options.crop,
            fit: options.fit,
        } as ImageUrlCreatorOptions;

        let output = item.path.toLowerCase().startsWith("http")
            ? item.path
            : `${mediaEndpoint}/${item.path}`;
        if (
            mediaEndpoint.includes("imgix") &&
            (_options.imageWidth || _options.imageHeight || _options.disabled)
        ) {
            output += `?${imgixQuery(_options)}`;
        }
        return output as ImageUrl;
    }

    return imageUrlFactory;
}

export {
    getImageUrlFactory,
    imageUrlZodType,
    imgixQuery,
    isImageUrl,
    notImageUrl,
};
export type { ImageFormat, ImageUrl, ImageUrlCreatorOptions };
